import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, Eye, Heart, BarChart2, ExternalLink, Flame } from 'lucide-react';
import { marketApi } from '../../services/marketApi';
import { formatPrice } from '../../utils/formatters';

const DIMENSION_OPTIONS = [
  { value: 'make', label: 'Marque' },
  { value: 'model', label: 'Modele' },
  { value: 'body_type', label: 'Carrosserie' },
  { value: 'fuel_type', label: 'Carburant' },
  { value: 'price_bracket', label: 'Tranche de prix' },
  { value: 'region', label: 'Region' },
];

const SORT_OPTIONS = [
  { value: 'hotness_score', label: 'Score popularite' },
  { value: 'views', label: 'Vues' },
  { value: 'favorites', label: 'Favoris' },
  { value: 'count', label: 'Nombre annonces' },
];

const TRENDS_LIMIT = 20;
const TRENDING_LIMIT = 12;

const inputClass = 'w-full bg-dark-hover border border-dark-border rounded-lg px-3 py-2 text-primary text-sm focus:outline-none focus:border-carmin transition-colors';
const labelClass = 'block text-xs font-medium text-secondary mb-1';

const hotnessColor = (score) => {
  if (score > 70) return 'text-green-400';
  if (score >= 50) return 'text-amber-400';
  return 'text-red-400';
};

const hotnessBg = (score) => {
  if (score > 70) return 'bg-green-500/20 text-green-400 border-green-500/30';
  if (score >= 50) return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
  return 'bg-red-500/20 text-red-400 border-red-500/30';
};

const Spinner = () => (
  <div className="flex justify-center py-10">
    <span className="h-6 w-6 border-2 border-carmin/30 border-t-carmin rounded-full animate-spin" />
  </div>
);

const DemandTab = () => {
  // Shared filters
  const [makeFilter, setMakeFilter] = useState('');

  // Trends state
  const [dimension, setDimension] = useState('make');
  const [sortBy, setSortBy] = useState('hotness_score');
  const [trendsOffset, setTrendsOffset] = useState(0);
  const [trendsData, setTrendsData] = useState(null);
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [trendsError, setTrendsError] = useState('');

  // Trending state
  const [trendingOffset, setTrendingOffset] = useState(0);
  const [trendingData, setTrendingData] = useState(null);
  const [trendingLoading, setTrendingLoading] = useState(false);
  const [trendingError, setTrendingError] = useState('');

  // Fetch demand trends
  const fetchTrends = useCallback(async () => {
    setTrendsLoading(true);
    setTrendsError('');
    try {
      const params = {
        dimension,
        sort_by: sortBy,
        limit: TRENDS_LIMIT,
        offset: trendsOffset,
      };
      if (makeFilter.trim()) params.make = makeFilter.trim();
      const data = await marketApi.getDemandTrends(params);
      setTrendsData(data);
    } catch (err) {
      setTrendsError(err.response?.data?.detail || 'Erreur lors du chargement des tendances');
    } finally {
      setTrendsLoading(false);
    }
  }, [dimension, sortBy, trendsOffset, makeFilter]);

  // Fetch trending vehicles
  const fetchTrending = useCallback(async () => {
    setTrendingLoading(true);
    setTrendingError('');
    try {
      const params = {
        limit: TRENDING_LIMIT,
        offset: trendingOffset,
      };
      if (makeFilter.trim()) params.make = makeFilter.trim();
      const data = await marketApi.getTrending(params);
      setTrendingData(data);
    } catch (err) {
      setTrendingError(err.response?.data?.detail || 'Erreur lors du chargement des vehicules tendance');
    } finally {
      setTrendingLoading(false);
    }
  }, [trendingOffset, makeFilter]);

  useEffect(() => { fetchTrends(); }, [fetchTrends]);
  useEffect(() => { fetchTrending(); }, [fetchTrending]);

  // Reset offsets when filters change
  const handleMakeChange = (val) => {
    setMakeFilter(val);
    setTrendsOffset(0);
    setTrendingOffset(0);
  };

  const handleDimensionChange = (val) => {
    setDimension(val);
    setTrendsOffset(0);
  };

  const handleSortChange = (val) => {
    setSortBy(val);
    setTrendsOffset(0);
  };

  // Pagination helpers
  const trendsTotal = trendsData?.total ?? 0;
  const trendsHasNext = trendsOffset + TRENDS_LIMIT < trendsTotal;
  const trendsHasPrev = trendsOffset > 0;

  const trendingTotal = trendingData?.total ?? 0;
  const trendingHasNext = trendingOffset + TRENDING_LIMIT < trendingTotal;
  const trendingHasPrev = trendingOffset > 0;

  return (
    <div className="space-y-6">
      {/* === Section 1: Demand Trends === */}
      <div className="bg-dark-card border border-dark-border rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2 text-primary font-semibold text-sm">
          <BarChart2 className="h-4 w-4 text-carmin" />
          Tendances de la demande
        </div>

        {/* Filters */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className={labelClass}>Dimension</label>
            <select
              value={dimension}
              onChange={(e) => handleDimensionChange(e.target.value)}
              className={inputClass}
            >
              {DIMENSION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Marque</label>
            <input
              type="text"
              value={makeFilter}
              onChange={(e) => handleMakeChange(e.target.value)}
              placeholder="ex: BMW"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Trier par</label>
            <select
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value)}
              className={inputClass}
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <span className="text-xs text-muted pb-2">
              {trendsTotal} resultat{trendsTotal !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Trends table */}
        {trendsLoading ? (
          <Spinner />
        ) : trendsError ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">
            {trendsError}
          </div>
        ) : trendsData?.results?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-dark-border text-left">
                  <th className="pb-2 text-xs font-medium text-muted">Groupe</th>
                  <th className="pb-2 text-xs font-medium text-muted text-right">
                    <span className="inline-flex items-center gap-1"><Flame className="h-3 w-3" /> Score</span>
                  </th>
                  <th className="pb-2 text-xs font-medium text-muted text-right">
                    <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> Vues</span>
                  </th>
                  <th className="pb-2 text-xs font-medium text-muted text-right">
                    <span className="inline-flex items-center gap-1"><Heart className="h-3 w-3" /> Favoris</span>
                  </th>
                  <th className="pb-2 text-xs font-medium text-muted text-right">Annonces</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-border">
                {trendsData.results.map((row, i) => (
                  <tr key={i} className="hover:bg-dark-hover/50 transition-colors">
                    <td className="py-2.5 text-primary font-medium">{row.group}</td>
                    <td className="py-2.5 text-right">
                      <span className={`font-semibold ${hotnessColor(row.hotness_score?.median)}`}>
                        {row.hotness_score?.median ?? '-'}
                      </span>
                    </td>
                    <td className="py-2.5 text-right text-secondary">
                      {row.views?.total?.toLocaleString('fr-BE') ?? '-'}
                    </td>
                    <td className="py-2.5 text-right text-secondary">
                      {row.favorites?.total?.toLocaleString('fr-BE') ?? '-'}
                    </td>
                    <td className="py-2.5 text-right text-secondary">
                      {row.count ?? '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center text-muted text-sm py-6">Aucune donnee disponible</p>
        )}

        {/* Trends pagination */}
        {trendsTotal > TRENDS_LIMIT && (
          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setTrendsOffset(Math.max(0, trendsOffset - TRENDS_LIMIT))}
              disabled={!trendsHasPrev}
              className="text-xs text-secondary hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Precedent
            </button>
            <span className="text-xs text-muted">
              {trendsOffset + 1} - {Math.min(trendsOffset + TRENDS_LIMIT, trendsTotal)} sur {trendsTotal}
            </span>
            <button
              onClick={() => setTrendsOffset(trendsOffset + TRENDS_LIMIT)}
              disabled={!trendsHasNext}
              className="text-xs text-secondary hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Suivant
            </button>
          </div>
        )}
      </div>

      {/* === Section 2: Trending Vehicles === */}
      <div className="bg-dark-card border border-dark-border rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-primary font-semibold text-sm">
            <TrendingUp className="h-4 w-4 text-carmin" />
            Vehicules tendance
          </div>
          <span className="text-xs text-muted">
            {trendingTotal} vehicule{trendingTotal !== 1 ? 's' : ''}
          </span>
        </div>

        {trendingLoading ? (
          <Spinner />
        ) : trendingError ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">
            {trendingError}
          </div>
        ) : trendingData?.results?.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {trendingData.results.map((item, i) => {
              const v = item.vehicle || {};
              const name = [v.make, v.model, v.year].filter(Boolean).join(' ');
              const isPopular = item.hotness_score > 70;

              return (
                <div
                  key={i}
                  className="bg-dark-hover border border-dark-border rounded-xl p-4 space-y-2 hover:border-carmin/40 transition-colors"
                >
                  {/* Header: name + badge */}
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="text-sm font-semibold text-primary leading-tight">{name || 'Vehicule inconnu'}</h4>
                    {isPopular && (
                      <span className="shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full border bg-green-500/20 text-green-400 border-green-500/30">
                        Populaire
                      </span>
                    )}
                  </div>

                  {/* Price + city */}
                  <div className="flex items-center justify-between">
                    <span className="text-base font-bold text-primary">{formatPrice(item.price)}</span>
                    {item.city && (
                      <span className="text-xs text-muted">{item.city}</span>
                    )}
                  </div>

                  {/* Stats row */}
                  <div className="flex items-center gap-4 text-xs text-secondary">
                    <span className="inline-flex items-center gap-1">
                      <Eye className="h-3 w-3 text-muted" />
                      {item.views?.toLocaleString('fr-BE') ?? '-'}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Heart className="h-3 w-3 text-muted" />
                      {item.favorites?.toLocaleString('fr-BE') ?? '-'}
                    </span>
                    <span className={`inline-flex items-center gap-1 font-medium ${hotnessColor(item.hotness_score)}`}>
                      <Flame className="h-3 w-3" />
                      {item.hotness_score ?? '-'}
                    </span>
                  </div>

                  {/* Days online + link */}
                  <div className="flex items-center justify-between pt-1">
                    {item.days_online != null && (
                      <span className="text-[10px] text-muted">
                        En ligne depuis {item.days_online}j
                      </span>
                    )}
                    {item.url && (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[10px] text-carmin hover:text-carmin/80 transition-colors"
                      >
                        Voir <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-center text-muted text-sm py-6">Aucun vehicule tendance</p>
        )}

        {/* Trending pagination */}
        {trendingTotal > TRENDING_LIMIT && (
          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setTrendingOffset(Math.max(0, trendingOffset - TRENDING_LIMIT))}
              disabled={!trendingHasPrev}
              className="text-xs text-secondary hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Precedent
            </button>
            <span className="text-xs text-muted">
              {trendingOffset + 1} - {Math.min(trendingOffset + TRENDING_LIMIT, trendingTotal)} sur {trendingTotal}
            </span>
            <button
              onClick={() => setTrendingOffset(trendingOffset + TRENDING_LIMIT)}
              disabled={!trendingHasNext}
              className="text-xs text-secondary hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Suivant
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemandTab;
